import { RuleSet, css } from 'styled-components';
import ITheme from '../@types/design-system/ITheme';
import { BreakpointsSizes } from '../@types/design-system/break-boints';

class Theme implements ITheme {
  constructor(
    colors: Partial<ITheme['colors']> = {},
    typography: Partial<ITheme['typography']> = {},
    breakpoints: Partial<ITheme['breakpoints']> = {}
  ) {
    this.colors = Object.assign(this.colors, colors);
    this.typography = Object.assign(this.typography, typography);
    this.breakpoints = Object.assign(this.breakpoints, breakpoints);
    this.typography = Object.assign(this.typography, {
      ...typography
    });

    Object.freeze(this);
  }

  colors = {
    grayscale: {
      10: '#0C0C0C',
      20: '#333',
      30: '#3D3D3D',
      40: '#555',
      50: '#6B6B6B',
      60: '#858585',
      70: '#9A9A9A',
      80: '#B8B8B8',
      90: '#D6D6D6',
      100: '#F2F2F2',
      110: '#FBFBFB'
    },
    modules: {
      1: {
        10: '#FFCB04',
        20: '#FFD32B',
        30: '#FFD843',
        40: '#FFDC56',
        50: '#FFE274',
        60: '#FFE78B',
        70: '#FFEDA6',
        80: '#FFF2BF',
        90: '#FFF6D4',
        100: '#FFFCF0'
      },
      2: {
        10: '#D86556',
        20: '#EA796A',
        30: '#F48A7C',
        40: '#FE9A8D',
        50: '#FFA99D',
        60: '#FFB8AE',
        70: '#FFC1B9',
        80: '#FFCDC7',
        90: '#FFE0DC',
        100: '#FFF7F5'
      },
      3: {
        10: '#7D8F0D',
        20: '#A9BF26',
        30: '#BED630',
        40: '#CEE544',
        50: '#D5EC4F',
        60: '#DEF45C',
        70: '#E6FC68',
        80: '#ECFF7E',
        90: '#F6FFBF',
        100: '#FCFFEC'
      },
      4: {
        10: '#337A99',
        20: '#6EBEE0',
        30: '#8DD7F7',
        40: '#9CE1FF',
        50: '#ACE6FF',
        60: '#BCEBFF',
        70: '#CEF0FF',
        80: '#D7F3FF',
        90: '#E0F6FF',
        100: '#F4FCFF'
      }
    },
    subjects: {
      portuguese: {
        10: '#242268',
        20: '#524FA1',
        30: '#908DD6',
        40: '#E5E4FF',
        50: '#F2F1FF',
        60: '#B2AFFF',
        70: '#EBFFFE',
      },
      art: {
        10: '#79003C',
        20: '#EC0677',
        30: '#FF8FC6',
        40: '#FFD1E8',
        50: '#FFF2F9'
      },
      science: {
        10: '#004B76',
        20: '#007DC5',
        30: '#45BBFF',
        40: '#CFEEFF',
        50: '#EEF9FF'
      },
      history: {
        10: '#9B2A00',
        20: '#F05A22',
        30: '#FFAE8F',
        40: '#FFE5DB',
        50: '#FFF3EF'
      },
      geography: {
        10: '#00421B',
        20: '#008636',
        30: '#3BC974',
        40: '#B7FFD4',
        50: '#E1FFED'
      },
      math: {
        10: '#680759',
        20: '#A2238E',
        30: '#ED80DC',
        40: '#FFD7F9',
        50: '#FFEFFC'
      }
    },
    gradients: {
      portuguese: 'linear-gradient(90deg, #504cc0 0%, #ff7776 100%)',
      art: 'linear-gradient(90deg, #ff78ba 0%, #c636af 100%)',
      science: 'linear-gradient(90deg, #359cd8 0%, #c636af 100%)',
      history: 'linear-gradient(90deg, #ff7f50 0%, #ffd946 100%)',
      geography: 'linear-gradient(90deg, #29bf63 0%, #c636af 100%)',
      math: 'linear-gradient(90deg, #bd28a6 0%, #ff8383 100%)'
    },
    marks: {
      1: '#FFFCC1',
      2: '#CCFFFF',
      3: '#CBF0FE',
      4: '#E2FFCA',
      5: '#FFDBF7',
      6: '#FFD8D8'
    }
  };

  spacing = {
    articleHeader: css`
      padding: 48px 90px;

      @media (max-width: 1440px) {
        padding: 40px 85px;
      }

      @media (max-width: 999px) {
        padding: 36px 54px;
      }

      @media (max-width: 600px) {
        padding: 24px 20px;
      }
    `,
    articleBody: css`
      padding: 0 179px;

      @media (max-width: 1440px) {
        padding: 0 170px;
      }

      @media (max-width: 999px) {
        padding: 0 92px;
      }

      @media (max-width: 600px) {
        padding: 0 20px;
      }

      &:has(.highlight-content) {
        padding: 0;
      }
    `,
  };

  typography = {
    h1: css`
      font-family: "Museo";
      font-size: 2.3rem;
      font-style: normal;
      font-weight: 900;
      line-height: 3.5rem;
    `,
    h2: css`
      font-family: "Museo";
      font-size: 1.6rem;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
    `,
    h3: css`
      font-family: "Museo";
      font-size: 1.4rem;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
    `,
    h4: css`
      font-family: "Museo";
      font-size: 1.3rem;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
    `,
    h5: css`
      font-family: "Museo";
      font-size: 1.2rem;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
    `,
    h6: css`
      font-family: "Museo";
      font-size: 1.1rem;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
    `,
    chapter: css`
      font-family: "Open Sans";
      font-size: 1.7rem;
      font-style: normal;
      font-weight: 700;
      line-height: 1.7rem; /* 100% */
    `,
    lesson: css`
      font-family: "Open Sans";
      font-size: 1.2rem;
      font-style: normal;
      font-weight: 700;
      line-height: 1rem; /* 83.333% */
    `,
    normal: css`
      font-family: "Open Sans", sans-serif;
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.6rem;
      letter-spacing: 0em;
      text-align: left;
    `,
    quote: css`
      font-family: "Museo";
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.6rem;
      letter-spacing: 0em;
      text-align: left;
    `,
    subtitle: css`
      font-family: "Museo";
      font-size: 0.7rem;
      font-weight: 400;
      line-height: 1.2rem;
      letter-spacing: 0em;
      text-align: left;
    `,
    font: css`
      font-family: "Museo";
      font-size: 0.7rem;
      font-weight: 300;
      line-height: 1rem;
      letter-spacing: 0em;
      text-align: left;
    `,
    modalBody: css`
      font-family: "Open Sans", sans-serif;
      font-size: 0.8rem;
      font-weight: 400;
      line-height: 1.2rem;
      letter-spacing: 0em;
      text-align: left;
    `,
    body: css``
  };

  breakpoints = {
    mobile: '24.375rem',
    tablet: '52.125rem',
    laptop: '80rem'
  };

  respondTo(
    breakpoint: BreakpointsSizes | BreakpointsSizes[],
    style: RuleSet<object>
  ): RuleSet<object> {
    const { mobile, tablet, laptop } = this.breakpoints;

    const queryMap: Record<BreakpointsSizes, string> = {
      mobile: `(max-width: ${mobile})`,
      tablet: `(min-width: calc(${mobile} + 1px)) and (max-width: ${tablet})`,
      laptop: `(min-width: calc(${tablet} + 1px)) and (max-width: ${laptop})`
    };

    const generateCss = (breakpoints: BreakpointsSizes): RuleSet<object> => {
      const mediaQuery = queryMap[breakpoints];
      return mediaQuery
        ? css`
            @media ${mediaQuery} {
              ${style}
            }
          `
        : css`
            ${style}
          `;
    };

    if (Array.isArray(breakpoint)) {
      return breakpoint.map(generateCss);
    } else {
      return generateCss(breakpoint);
    }
  }
}

export default Theme;
